import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, KeyRound, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { createApplication, createCheckoutSession } from "@/lib/api";
import StepPersonal from "@/components/apply/StepPersonal";
import StepResidence from "@/components/apply/StepResidence";
import StepEmployment from "@/components/apply/StepEmployment";
import StepReferences from "@/components/apply/StepReferences";
import StepBackground from "@/components/apply/StepBackground";
import StepReview from "@/components/apply/StepReview";

const STEPS = [
  { id: "personal", label: "Personal", Component: StepPersonal },
  { id: "residence", label: "Residence", Component: StepResidence },
  { id: "employment", label: "Employment", Component: StepEmployment },
  { id: "references", label: "References", Component: StepReferences },
  { id: "background", label: "Background", Component: StepBackground },
  { id: "review", label: "Review & Pay", Component: StepReview },
];

const initialForm = {
  property_applying_for: "",
  lease_length_months: 12,
  preferred_move_in_date: "",
  full_name: "",
  email: "",
  phone: "",
  date_of_birth: "",
  number_of_adults: 1,
  has_pets: false,
  pet_breeds: "",
  current_address: "",
  current_landlord_phone: "",
  years_at_current_address: 0,
  occupation: "",
  supervisor_name: "",
  supervisor_phone: "",
  job_title: "",
  monthly_income: 0,
  reference_1: { name: "", phone: "", relation: "" },
  reference_2: { name: "", phone: "", relation: "" },
  reference_3: { name: "", phone: "", relation: "" },
  convicted_felony_7yrs: false,
  felony_explanation: "",
  evicted_or_asked_to_vacate: false,
  eviction_explanation: "",
  certification_agreed: false,
};

export default function Apply() {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const progress = useMemo(
    () => ((stepIdx + 1) / STEPS.length) * 100,
    [stepIdx]
  );

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateRef = (key, patch) =>
    setForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));

  const validateStep = () => {
    switch (STEPS[stepIdx].id) {
      case "personal":
        if (!form.property_applying_for.trim()) {
          toast.error("Please enter the address you're applying for.");
          return false;
        }
        if (!form.preferred_move_in_date) {
          toast.error("Please choose a preferred move-in date.");
          return false;
        }
        if (![6, 9, 12].includes(Number(form.lease_length_months))) {
          toast.error("Please select a lease length.");
          return false;
        }
        if (!form.full_name || !form.email || !form.phone || !form.date_of_birth) {
          toast.error("Please complete all personal fields.");
          return false;
        }
        if (form.has_pets && !form.pet_breeds.trim()) {
          toast.error("Please list your dog breed(s).");
          return false;
        }
        return true;
      case "residence":
        if (
          !form.current_address ||
          !form.current_landlord_phone ||
          form.years_at_current_address === "" ||
          form.years_at_current_address === null
        ) {
          toast.error("Please complete all residence fields.");
          return false;
        }
        return true;
      case "employment":
        if (
          !form.occupation ||
          !form.supervisor_name ||
          !form.supervisor_phone ||
          !form.job_title ||
          !form.monthly_income
        ) {
          toast.error("Please complete all employment fields.");
          return false;
        }
        return true;
      case "references": {
        const refs = [form.reference_1, form.reference_2, form.reference_3];
        if (refs.some((r) => !r.name || !r.phone || !r.relation)) {
          toast.error("Please provide all three references.");
          return false;
        }
        return true;
      }
      case "background":
        if (form.convicted_felony_7yrs && !form.felony_explanation.trim()) {
          toast.error("Please provide an explanation for the felony question.");
          return false;
        }
        if (form.evicted_or_asked_to_vacate && !form.eviction_explanation.trim()) {
          toast.error("Please provide an eviction explanation.");
          return false;
        }
        if (!form.certification_agreed) {
          toast.error("Please certify your information and authorize verification.");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const next = () => {
    if (!validateStep()) return;
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const back = () => {
    setStepIdx((i) => Math.max(i - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmitAndPay = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        lease_length_months: Number(form.lease_length_months) || 12,
        number_of_adults: Number(form.number_of_adults) || 1,
        years_at_current_address: Number(form.years_at_current_address) || 0,
        monthly_income: Number(form.monthly_income) || 0,
      };
      const application = await createApplication(payload);
      const session = await createCheckoutSession(application.id);
      window.location.href = session.url;
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.detail || "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const CurrentStep = STEPS[stepIdx].Component;
  const isReview = STEPS[stepIdx].id === "review";

  return (
    <div className="min-h-screen bg-[#FAF6F0] text-[#2A2521]" data-testid="apply-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAF6F0]/80 border-b border-[#E8DCC8]/60">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#4A423B] hover:text-[#2A2521] transition"
            data-testid="apply-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-heading text-lg tracking-wide">Experian Properties</span>
          </Link>
          <div className="hidden sm:flex items-center gap-2 text-xs text-[#8B8378]">
            <Lock className="w-3.5 h-3.5" />
            Secure Application
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="max-w-4xl mx-auto px-6 pt-10">
        <div className="flex items-center justify-between mb-3">
          <p className="label-eyebrow text-[#A67C2E]">
            Step {stepIdx + 1} of {STEPS.length}
          </p>
          <p className="label-eyebrow">{STEPS[stepIdx].label}</p>
        </div>
        <div className="h-[2px] bg-white/10 rounded-full overflow-hidden">
          <div
            className="progress-fill h-full bg-[#A67C2E]"
            style={{ width: `${progress}%` }}
            data-testid="apply-progress-bar"
          />
        </div>

        {/* Stepper labels */}
        <div className="hidden md:flex mt-6 justify-between text-[11px] uppercase tracking-widest text-[#8B8378]">
          {STEPS.map((s, i) => (
            <span
              key={s.id}
              className={`flex items-center gap-2 ${
                i <= stepIdx ? "text-[#A67C2E]" : ""
              }`}
            >
              {i < stepIdx ? <Check className="w-3 h-3" /> : <KeyRound className="w-3 h-3" />}
              {s.label}
            </span>
          ))}
        </div>
      </div>

      {/* Step content */}
      <main className="max-w-2xl mx-auto px-6 py-12 md:py-16">
        <AnimatePresence mode="wait">
          <motion.div
            key={STEPS[stepIdx].id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
          >
            <CurrentStep form={form} update={update} updateRef={updateRef} />
          </motion.div>
        </AnimatePresence>

        {/* Actions */}
        <div className="mt-14 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={back}
            disabled={stepIdx === 0}
            data-testid="apply-back-btn"
            className="inline-flex items-center gap-2 text-sm text-[#6B6560] hover:text-[#2A2521] disabled:opacity-30 disabled:cursor-not-allowed transition"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          {!isReview ? (
            <button
              type="button"
              onClick={next}
              data-testid="apply-next-btn"
              className="btn-gold inline-flex items-center gap-2 px-8 py-3.5 rounded-sm text-sm"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmitAndPay}
              disabled={submitting}
              data-testid="apply-submit-pay-btn"
              className="btn-gold inline-flex items-center gap-2 px-8 py-3.5 rounded-sm text-sm disabled:opacity-70"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Redirecting to Payment…
                </>
              ) : (
                <>
                  Submit & Pay $50 <Lock className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
